/**
 * Dinero — céntimos de colón, siempre entero.
 *
 * `₡65.000` es `6_500_000`. Nunca se usa punto flotante para representar
 * dinero, y nunca se opera con dinero en el navegador: allá solo se formatea.
 *
 * Por qué céntimos y no colones enteros: el negocio liquida en colones —el
 * reparto redondea a `COLON` y las partes caen exactas— pero en el histórico
 * real existe un importe con céntimos (`₡53.097,35`). Guardarlo fiel cuesta
 * cero y evita que el sistema mienta sobre un dato que sí conocemos.
 */

/** Un monto en céntimos de colón. */
export type Minor = number;

/** Un colón. Es la unidad de liquidación por defecto del reparto. */
export const COLON = 100;

export function isMinor(v: unknown): v is Minor {
  return typeof v === 'number' && Number.isSafeInteger(v);
}

export function assertMinor(v: unknown, what = 'monto'): Minor {
  if (!isMinor(v)) throw new TypeError(`${what} debe ser un entero de céntimos, llegó ${String(v)}`);
  return v;
}

/** Céntimos → colones, redondeando a la unidad indicada. */
export function roundTo(amount: Minor, unit: Minor = COLON): Minor {
  if (!Number.isSafeInteger(unit) || unit <= 0) throw new RangeError(`unidad inválida: ${unit}`);
  return Math.round(amount / unit) * unit;
}

/** Aplica una tasa (0.13) a un monto y redondea a la unidad de liquidación. */
export function applyRate(amount: Minor, rate: number, unit: Minor = COLON): Minor {
  if (!(rate >= 0 && rate < 1)) throw new RangeError(`tasa fuera de rango: ${rate}`);
  return roundTo(Math.round(amount * rate), unit);
}

/** Aplica un porcentaje (50 = 50%) y redondea a la unidad de liquidación. */
export function applyPercent(amount: Minor, percent: number, unit: Minor = COLON): Minor {
  if (!(percent >= 0)) throw new RangeError(`porcentaje negativo: ${percent}`);
  return roundTo(Math.round((amount * percent) / 100), unit);
}

/* ────────────────────────────────────────────────────────────────────────────
   Reparto por resto mayor (Hamilton)
   ──────────────────────────────────────────────────────────────────────────── */

/** Escala de los pesos: soporta porcentajes con cuatro decimales. */
const WEIGHT_SCALE = 10_000n;

/**
 * Reparte `total` entre `weights` por el método del resto mayor.
 *
 * Se llama **una vez por componente** de dinero (subtotal, descuento,
 * impuesto) con los **mismos pesos**. Así cada componente suma exacto y, por
 * transitividad, el total también: no hace falta absorber el residuo en
 * ninguna parte.
 *
 * Todo el cálculo es aritmética entera **en `BigInt`**. Los pesos se escalan a
 * enteros antes de dividir, de modo que el desempate no depende de un error de
 * punto flotante y la previsualización es idéntica a la generación. Se usa
 * `BigInt` y no `number` porque los pesos también pueden ser montos en
 * céntimos: `unidades × peso` se sale de `2^53` con cifras perfectamente
 * normales, y ahí un reparto empieza a perder colones en silencio.
 *
 * El desempate final es el orden de entrada, que es el `ordinal` que controla
 * el usuario.
 *
 *     splitAmount(10_000_000, [1, 1, 1])  →  [3_333_400, 3_333_300, 3_333_300]
 *     // ₡100.000 entre 3 → ₡33.334 / ₡33.333 / ₡33.333, suma exacta
 */
export function splitAmount(total: Minor, weights: readonly number[], unit: Minor = COLON): Minor[] {
  const n = weights.length;
  if (n === 0) return [];
  if (!Number.isSafeInteger(unit) || unit <= 0) throw new RangeError(`unidad de redondeo inválida: ${unit}`);
  if (!Number.isFinite(total)) throw new RangeError(`total inválido: ${total}`);

  const scaled = weights.map((w) => {
    if (!Number.isFinite(w) || w < 0) throw new RangeError('los pesos no pueden ser negativos');
    return BigInt(Math.round(w * Number(WEIGHT_SCALE)));
  });

  const wSum = scaled.reduce((a, b) => a + b, 0n);
  if (wSum === 0n) throw new RangeError('los pesos suman cero');

  const units = BigInt(Math.round(total / unit));
  const base = new Array<bigint>(n);
  const remainder = new Array<bigint>(n);
  let assigned = 0n;

  for (let i = 0; i < n; i++) {
    const numerator = units * scaled[i]!;
    const q = numerator / wSum;              // división entera de BigInt: trunca
    base[i] = q;
    remainder[i] = numerator - q * wSum;     // exacto, por construcción
    assigned += q;
  }

  // Siempre 0 <= leftover < n.
  const leftover = Number(units - assigned);
  const order = base
    .map((_, i) => i)
    .sort((a, b) => {
      const d = remainder[b]! - remainder[a]!;
      return d > 0n ? 1 : d < 0n ? -1 : a - b;
    });

  for (let k = 0; k < leftover; k++) base[order[k]!] = base[order[k]!]! + 1n;

  return base.map((b) => Number(b) * unit);
}

/* ────────────────────────────────────────────────────────────────────────────
   Formato
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * `6_500_000` → `"₡65,000"`. Solo para mostrar.
 *
 * Coma para los miles y punto para los céntimos, que es como está escrito el
 * brief y como se factura en Costa Rica.
 *
 * Se formatea a mano en vez de con `Intl`: el ICU del Worker, el de Node y el
 * del navegador no coinciden —`es-CR` produce espacio fino en algunos— y el
 * mismo monto no puede verse distinto según dónde se renderice. Los céntimos
 * solo aparecen si existen, para que `₡65,000` no se llene de ceros inútiles.
 */
export function formatMoney(minor: Minor, opts: { symbol?: boolean; cents?: boolean } = {}): string {
  const symbol = opts.symbol ?? true;
  const negative = minor < 0;
  const abs = Math.abs(minor);
  const whole = Math.trunc(abs / 100);
  const cents = abs % 100;

  const grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const showCents = opts.cents ?? cents !== 0;
  const decimals = showCents ? `.${String(cents).padStart(2, '0')}` : '';

  return `${negative ? '−' : ''}${symbol ? '₡' : ''}${grouped}${decimals}`;
}

/**
 * El dinero cruza el borde JSON como **texto de céntimos**, nunca como número:
 * un entero grande sigue siendo exacto en un double, pero el texto elimina de
 * raíz la tentación de operarlo en el cliente.
 */
export function toWire(minor: Minor): string {
  return String(assertMinor(minor));
}

export function fromWire(wire: string | number): Minor {
  const n = typeof wire === 'number' ? wire : Number.parseInt(wire, 10);
  return assertMinor(n);
}

/** Lee un monto escrito por una persona: `"65.000,50"`, `"₡65 000"`, `"65000.5"`. */
export function parseMoneyInput(raw: string): Minor | null {
  const cleaned = raw.replace(/[₡\s]/g, '').trim();
  if (cleaned === '') return null;

  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');
  let normalized: string;

  if (lastComma === -1 && lastDot === -1) {
    normalized = cleaned;
  } else {
    // El separador decimal es el último que aparece; el otro es de miles.
    const decimalAt = Math.max(lastComma, lastDot);
    const intPart = cleaned.slice(0, decimalAt).replace(/[.,]/g, '');
    const fracPart = cleaned.slice(decimalAt + 1);
    // Tres dígitos después del último separador y sin otro separador antes:
    // era separador de miles, no decimal. `65.000` son sesenta y cinco mil.
    normalized = fracPart.length === 3 && !/[.,]/.test(cleaned.slice(0, decimalAt))
      ? intPart + fracPart
      : `${intPart}.${fracPart}`;
  }

  if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null;
  return Math.round(Number.parseFloat(normalized) * 100);
}
